import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useRegisterInput from '@afosto/app-components-react/Form/useRegisterInput';
import Button from '@material-ui/core/Button';
import CollapseAdjustment from '../../CollapseAdjustment';
import ContentDrawer from '@afosto/app-components-react/ContentDrawer';
import Form from '@afosto/app-components-react/Form';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import removeObjectFromArray from '@afosto/utils/array/removeObjectFromArray';
import updateObjectInArray from '@afosto/utils/array/updateObjectInArray';
import uuid from '@afosto/utils/uuid';
import validationSchema from './validations';
import translations from './translations';

const AdjustmentsDrawer = ({ adjustments, isOpen, onClose, onSave }) => {
  const intl = useIntl();
  const [activeAdjustmentID, setActiveAdjustmentID] = useState(null);

  const form = useForm({
    validationSchema,
    defaultValues: {
      adjustments: adjustments ?? [],
    },
    onSubmit: onSave,
    onCancel: onClose,
  });
  const { setValue, watch } = form;
  const adjustmentsValue = watch('adjustments');

  useRegisterInput('adjustments', true, form);

  /**
   * Edit adjustment data.
   * @param {Object} adjustment
   */
  const editAdjustment = adjustment => {
    setValue('adjustments', updateObjectInArray(adjustmentsValue, adjustment, 'id'));
  };

  /**
   * Select an adjustment to edit / create.
   * @param {number|null} adjustmentID
   */
  const selectAdjustment = (adjustmentID = null) => () => {
    if (activeAdjustmentID !== adjustmentID) {
      setActiveAdjustmentID(adjustmentID);
    } else {
      setActiveAdjustmentID(null);
    }
  };

  /**
   * Remove an adjustment by ID.
   * @param {string|null} adjustmentID
   */
  const removeAdjustment = (adjustmentID = null) => () => {
    if (adjustmentID) {
      const otherValues = removeObjectFromArray(adjustmentsValue, { id: adjustmentID }, 'id');
      setValue('adjustments', otherValues);
    }
  };

  /**
   * Add new empty adjustment.
   */
  const addEmptyAdjustment = () => {
    const generatedID = uuid();

    setValue('adjustments', [
      ...adjustmentsValue,
      {
        id: generatedID,
        description: '',
        isDiscount: true,
        amount: '',
        type: 'amount',
      },
    ]);

    setActiveAdjustmentID(generatedID);
  };

  return (
    <ContentDrawer
      title={intl.formatMessage(translations.title)}
      isOpen={isOpen}
      handleClose={onClose}
      drawerWidth="400px"
    >
      <LoadingWrapper isLoading={form.isSubmitting}>
        <Form form={form}>
          {adjustmentsValue.map(adjustment => (
            <CollapseAdjustment
              key={adjustment.id}
              isOpen={activeAdjustmentID === adjustment.id}
              onSelect={selectAdjustment(adjustment.id)}
              inline={adjustment?.result?.is_applied_rule}
              readOnly={adjustment?.result?.is_applied_rule ?? false}
              isEditing={!adjustment?.result?.is_applied_rule}
              renderActions={false}
              onChange={editAdjustment}
              adjustment={adjustment}
              onRemove={removeAdjustment(adjustment.id)}
              removable={!adjustment?.result?.is_applied_rule}
            />
          ))}
          <Button onClick={addEmptyAdjustment}>
            {intl.formatMessage(translations.actions.add.label)}
          </Button>
        </Form>
      </LoadingWrapper>
    </ContentDrawer>
  );
};

AdjustmentsDrawer.propTypes = {
  adjustments: PropTypes.array,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
};

AdjustmentsDrawer.defaultProps = {
  adjustments: [],
  isOpen: false,
  onClose: undefined,
  onSave: undefined,
};

export default AdjustmentsDrawer;
